import { ApiVersions } from '../api-versions';
import type { GetTokensOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export const apiVersion = ApiVersions.V4;

export type GetTokensQueryParams = Omit<GetTokensOpenAPIQueryParams, 'filter' | 'sort'> & {
  filter?: {
    type?: 'service' | 'personal';
    creator?: string;
    query?: string;
    /** `true` returns only expired tokens; `false` only unexpired (in-use). */
    expired?: boolean;
    expiresBy?: number;
    apiVersion?: number;
  };
  /**
   * `-` prefix sorts descending. `expiry` is only accepted while the
   * `enableNewAccessTokenExpiryField` flag is on. There is no `lastUsed`
   * ordering — last-used times are stored outside the tokens table and merged
   * into each page after the query runs, so the API can't order by them.
   */
  sort?: 'name' | '-name' | 'creationDate' | '-creationDate' | 'lastModified' | '-lastModified' | 'expiry' | '-expiry';
};

export function querySerializer(queryParams: GetTokensQueryParams) {
  const searchParams = new URLSearchParams();

  if (queryParams.limit !== undefined && queryParams.limit > 0) {
    searchParams.set('limit', queryParams.limit.toString());
  }

  if (queryParams.offset !== undefined && queryParams.offset > 0) {
    searchParams.set('offset', queryParams.offset.toString());
  }

  if (queryParams.showAll === true) {
    searchParams.set('showAll', 'true');
  }

  if (queryParams.filter) {
    const serializedFilter = serializeTokensFilterParam(queryParams.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (queryParams.sort) {
    searchParams.set('sort', queryParams.sort);
  }

  return searchParams.toString();
}

function serializeTokensFilterParam(value: NonNullable<GetTokensQueryParams['filter']>) {
  const filter: string[] = [];

  if (value.type) {
    filter.push(`type:${value.type}`);
  }

  if (value.creator) {
    filter.push(`creator:${value.creator}`);
  }

  if (value.query) {
    filter.push(`query:${value.query}`);
  }

  if (value.expired !== undefined) {
    filter.push(`expired:${value.expired}`);
  }

  if (value.expiresBy !== undefined) {
    filter.push(`expiresBy:${value.expiresBy}`);
  }

  if (value.apiVersion) {
    filter.push(`defaultApiVersion:${value.apiVersion}`);
  }

  return filter.join(',');
}
